/**
 * PetroNexus — Global Toast System
 *
 * A lightweight, zero-dependency toast notification provider using MUI Snackbar.
 * Use `toast.success('Done!')` or `toast.error('Oops')` from anywhere in the app.
 *
 * Usage:
 *   import { toast } from '../lib/toast';
 *   toast.success('Tank created successfully');
 *   toast.error('Something went wrong');
 *   toast.info('Price sync starting...');
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { Snackbar, Alert, type AlertColor, Slide, type SlideProps } from '@mui/material';

interface ToastItem {
  id: number;
  message: string;
  severity: AlertColor;
}

interface ToastCtx {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastCtx>({
  success: () => {},
  error: () => {},
  info: () => {},
  warning: () => {},
});

function SlideUp(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timerRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const addToast = useCallback((message: string, severity: AlertColor) => {
    const id = ++nextId;
    setToasts((prev) => [...prev, { id, message, severity }]);
    timerRef.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete timerRef.current[id];
    }, 4000);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timerRef.current[id]) {
      clearTimeout(timerRef.current[id]);
      delete timerRef.current[id];
    }
  }, []);

  const ctx: ToastCtx = {
    success: useCallback((msg: string) => addToast(msg, 'success'), [addToast]),
    error: useCallback((msg: string) => addToast(msg, 'error'), [addToast]),
    info: useCallback((msg: string) => addToast(msg, 'info'), [addToast]),
    warning: useCallback((msg: string) => addToast(msg, 'warning'), [addToast]),
  };

  useEffect(() => {
    setImperativeToast(ctx);
    return () => setImperativeToast({
      success: () => {},
      error: () => {},
      info: () => {},
      warning: () => {},
    });
  }, [ctx]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}

      {/* Stacked toasts — bottom-right corner */}
      {toasts.map((t, idx) => (
        <Snackbar
          key={t.id}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={SlideUp}
          sx={{ bottom: `${24 + idx * 64}px !important` }}
        >
          <Alert
            severity={t.severity}
            variant="filled"
            onClose={() => dismiss(t.id)}
            sx={{
              minWidth: 280,
              fontWeight: 600,
              borderRadius: 2.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              '& .MuiAlert-icon': { fontSize: 22 },
            }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
}

/** Hook to access toast functions inside React components */
export function useToast() {
  return useContext(ToastContext);
}

// ── Imperative API (for use outside React components) ────────────────────────
// Singleton reference wired up by ToastProvider

let _imperativeToast: ToastCtx | null = null;

export function setImperativeToast(ctx: ToastCtx) {
  _imperativeToast = ctx;
}

/** Imperative toast — works outside of React component tree */
export const toast: ToastCtx = {
  success: (msg) => _imperativeToast?.success(msg),
  error: (msg) => _imperativeToast?.error(msg),
  info: (msg) => _imperativeToast?.info(msg),
  warning: (msg) => _imperativeToast?.warning(msg),
};
